import { useCallback, useEffect, useState } from 'react';
import { isCurrentTimeInRange } from '@nykaa/ui-kit/component/CountDownTimer/utils';
import { isValidObject } from '@nykaa/utils/validations';
import { pushEvent } from '@nykaa/data-layer';
import { logger } from 'logging';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { useSelector } from 'react-redux';
import { selectCurrentSku } from 'store/product/selector';
import { Variant } from 'store/product/types';
import { State } from 'store/types';
import { useIsLoggedIn } from 'shared/hooks/auth';
import { AB_GETAPPNUDGE_MWEB, SECONDARY_TAGS_V2 } from 'constants/experiments';
import {
  NO_AF_FALLBACK_URL,
  NO_AF_URL,
} from 'layouts/Product/ProductInfo/helper';
import { appsFlyerLogs } from 'constants/productPage';

export const updatedPriceIfV5Applicable = (
  currentSku: Variant | undefined,
  differentialEnabled: boolean,
  v5Enabled: boolean
) => {
  const { offerPrice, discount, cohortSaleTemplateV2, mrp } = currentSku || {};
  let finalPrice = offerPrice;
  let finalDiscount = discount;
  let isPriceRevealV5TimerShown = false;
  if (
    differentialEnabled &&
    v5Enabled &&
    isValidObject(cohortSaleTemplateV2) &&
    cohortSaleTemplateV2?.showPinkBoxCTA === false &&
    cohortSaleTemplateV2?.startTime &&
    cohortSaleTemplateV2?.endTime
  ) {
    const { showSection } =
      isCurrentTimeInRange(
        cohortSaleTemplateV2?.startTime,
        cohortSaleTemplateV2?.endTime
      ) || {};
    isPriceRevealV5TimerShown = showSection;
  }

  if (isPriceRevealV5TimerShown) {
    const { price: salePrice, discountPercentage: saleDiscount } =
      cohortSaleTemplateV2 || {};
    if (salePrice && mrp > salePrice) {
      finalPrice = salePrice;
      finalDiscount = saleDiscount;
    }
  }
  return { finalPrice, finalDiscount };
};

export const useGetUpdatedPriceDiscount = () => {
  const isPRV5Enabled = useSelector(
    (state: State) => state.remoteConfig.switches.isPDPriceRevealEnabled
  );

  const isDifferential = useSelector(
    (state: State) => state?.remoteConfig?.switches?.differentialPricingEnabled
  );
  const currentSku = useSelector((state: State) => selectCurrentSku(state));
  return updatedPriceIfV5Applicable(currentSku, isDifferential, isPRV5Enabled);
};

export const useIsAccordionEligible = (expName: string | null | undefined) => {
  const experimentName = expName || '';
  const isSectionEligible = useSelector((state: State) =>
    getEligibleForVariant(state, experimentName, 'A')
  );
  const isPdpCollapsibleEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.isPdpCollapsibleEnabled
  );
  if (!isPdpCollapsibleEnabled) {
    return false;
  }
  if (!expName) {
    return true;
  }
  return isSectionEligible;
};

export const useAppsFlyerSmartScript = () => {
  const [afSmartScriptResult, setAfSmartScriptResult] = useState('');
  const [showShimmer, setShowShimmer] = useState(true);
  const afScriptFallbackTimer = useSelector(
    (state: State) => state.remoteConfig.configs.afScriptFallbackTimer
  );
  const appsFlyerFallbackClickUrl =
    useSelector(
      (state: State) =>
        state.remoteConfig.configs.downloadAppNudgeConfig
          ?.appsFlyerFallbackClickUrl
    ) || NO_AF_FALLBACK_URL;

  const handleFallbackTimeout = useCallback(() => {
    logger.info(appsFlyerLogs.SMART_SCRIPT_TIMEOUT);
    setAfSmartScriptResult(appsFlyerFallbackClickUrl);
    setShowShimmer(false);
  }, [appsFlyerFallbackClickUrl, setAfSmartScriptResult, setShowShimmer]);
  useEffect(() => {
    const globalTimeoutId = setTimeout(
      handleFallbackTimeout,
      afScriptFallbackTimer
    );

    const handleAfSmartScriptLoaded = () => {
      const smartScriptResult = (window as any)?.AF_SMART_SCRIPT_RESULT;
      const { clickURL } = smartScriptResult || {};
      clearTimeout(globalTimeoutId);
      if (clickURL) {
        logger.info(appsFlyerLogs.CLICK_URL_SUCCESS(clickURL));
        pushEvent(AB_GETAPPNUDGE_MWEB);
        setAfSmartScriptResult(clickURL);
        setShowShimmer(false);
      } else if (clickURL === NO_AF_URL) {
        logger.info(appsFlyerLogs.NO_CLICK_URL_FALLBACK);
        setAfSmartScriptResult(appsFlyerFallbackClickUrl);
        setShowShimmer(false);
      } else if (!clickURL) {
        handleFallbackTimeout();
      }
    };
    window.addEventListener('afSmartScriptLoaded', handleAfSmartScriptLoaded);

    return () => {
      window.removeEventListener(
        'afSmartScriptLoaded',
        handleAfSmartScriptLoaded
      );
      clearTimeout(globalTimeoutId);
    };
  }, [afScriptFallbackTimer, appsFlyerFallbackClickUrl, handleFallbackTimeout]);

  return { showShimmer, afSmartScriptResult, appsFlyerFallbackClickUrl };
};

export const useAppDownloadBanner = () => {
  const isLoggedIn = useIsLoggedIn();
  const { finalPrice = 0 } = useGetUpdatedPriceDiscount() || {};

  const isAppNudgeExpEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_GETAPPNUDGE_MWEB, 'A')
  );

  const allowNudgeForLoggedIn = useSelector(
    (state: State) => state.remoteConfig.switches.showAppNudgeLoggedIn
  );

  const downloadBannerConfig = useSelector(
    (state: State) => state.remoteConfig.configs.downloadAppNudgeConfig
  );

  const { showShimmer, afSmartScriptResult, appsFlyerFallbackClickUrl } =
    useAppsFlyerSmartScript();

  const shouldRenderBanner =
    isAppNudgeExpEnabled && (!isLoggedIn || allowNudgeForLoggedIn);

  const shouldShowShimmer = shouldRenderBanner && showShimmer;

  const {
    discount: discountPercent,
    minOrderValue = 0,
    maxDiscountAmount = 0,
    title,
    subtitle,
    cta,
    icon,
    backgroundImage,
  } = downloadBannerConfig || {};

  let appDiscountedPrice = finalPrice;

  if (finalPrice > minOrderValue) {
    const discountAmount = (Number(discountPercent) / 100) * finalPrice;
    // the final price ensuring the discount doesn't exceed maxDiscountAmount
    appDiscountedPrice = Math.max(
      finalPrice - discountAmount,
      finalPrice - maxDiscountAmount
    );
  }

  // const appDiscountedPrice =
  //   finalPrice && discountPercent
  //     ? Number(finalPrice) -
  //       (Number(discountPercent) / 100) * Number(finalPrice)
  //     : finalPrice;

  const discountAppliedTitle = title?.replace(
    '{discount}',
    `${Math.round(appDiscountedPrice ?? 0)}`
  );

  const onDownloadClick = () => {
    if (afSmartScriptResult) {
      window.location.href = afSmartScriptResult;
    } else {
      window.location.href = appsFlyerFallbackClickUrl;
    }
  };
  return {
    shouldShowShimmer,
    discountAppliedTitle,
    subtitle,
    cta,
    icon,
    backgroundImage,
    isAppNudgeExpEnabled,
    onDownloadClick,
    shouldRenderBanner,
  };
};

export const useIsTagsV2Enabled = () => {
  const tagsV2Enabled = useSelector((state: State) =>
    getEligibleForVariant(state, SECONDARY_TAGS_V2, 'A')
  );
  return tagsV2Enabled;
};
